import { useCallback } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useIsCustomThemesEnabled } from "@/react/components/SettingsApp/Site/Pages/hooks/useIsCustomThemesEnabled";
import { usePage } from "@/react/components/SettingsApp/Site/Pages/hooks/usePage";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import { useCustomThemeConfirmationModal } from "@circle-react-shared/BuilderUI/SiteThemes/CustomThemeConfirmationModal";
import { usePageThemeConfirmationModal } from "@circle-react-shared/BuilderUI/SiteThemes/PageThemeConfirmationModal";
import { useSiteThemeConfirmationModal } from "@circle-react-shared/BuilderUI/SiteThemes/SiteThemeConfirmationModal";
import type { FormData } from "../FormWrapper";

export const useFormActions = ({
  pageId,
  methods,
}: {
  pageId: number;
  methods: UseFormReturn<FormData>;
}) => {
  const { page, isLoading: isPageLoading } = usePage(pageId);
  const isCustomThemesEnabled = useIsCustomThemesEnabled();
  const { show: showPageThemeConfirmationModal } =
    usePageThemeConfirmationModal();
  const { show: showCustomThemeConfirmationModal } =
    useCustomThemeConfirmationModal();
  const { show: showSiteThemeConfirmationModal } =
    useSiteThemeConfirmationModal();

  const {
    reset,
    formState: { isDirty },
  } = methods;

  const appearance = useBuilderStore(state => state.appearance);
  const currentCanvasTheme = useBuilderStore(state => state.currentCanvasTheme);
  const toggleCurrentCanvasTheme = useBuilderStore(
    state => state.toggleCurrentCanvasTheme,
  );

  const cssVariables = currentCanvasTheme.cssVariables;

  const handleDiscard = useCallback(() => {
    if (isPageLoading || !page) return;

    const { id, theme_name, css_variables } = page.theme;

    toggleCurrentCanvasTheme({
      value: id,
      name: theme_name,
      cssVariables: css_variables,
    });

    reset({
      theme: css_variables,
    });
  }, [page, isPageLoading, toggleCurrentCanvasTheme, reset]);

  const handlePageThemUpdate = useCallback(() => {
    if (isDirty && isCustomThemesEnabled) {
      void showCustomThemeConfirmationModal({
        appearance,
        cssVariables,
        currentCanvasTheme,
      });
    } else {
      void showPageThemeConfirmationModal({
        appearance,
        pageId,
        currentCanvasTheme,
      });
    }
  }, [
    pageId,
    currentCanvasTheme,
    appearance,
    showPageThemeConfirmationModal,
    isDirty,
    cssVariables,
    isCustomThemesEnabled,
    showCustomThemeConfirmationModal,
  ]);

  const handleSiteThemeUpdate = () => {
    if (isDirty && isCustomThemesEnabled) {
      void showCustomThemeConfirmationModal({
        appearance,
        cssVariables,
        currentCanvasTheme,
        scope: "site",
      });
    } else {
      void showSiteThemeConfirmationModal({
        appearance,
        cssVariables,
        currentCanvasTheme,
      });
    }
  };

  return {
    handleDiscard,
    handlePageThemUpdate,
    isPageLoading,
    handleSiteThemeUpdate,
  };
};
